/**
 * Cover Image URL Utilities
 * 
 * Provides consistent cache-busted URLs for book cover images.
 * Ensures updated covers propagate immediately across the app.
 */

// Get backend URL from environment
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Generate a cache-busted cover URL for a manuscript
 * @param {string} manuscriptId - The manuscript ID
 * @param {string|number} updatedAt - The cover_updated_at timestamp (optional)
 * @returns {string|null} The cache-busted cover URL
 */
export const getCoverUrl = (manuscriptId, updatedAt = null) => {
  if (!manuscriptId) return null;
  
  const baseUrl = `${API_URL}/api/covers/${manuscriptId}`;
  
  // Add cache-buster if we have an updated timestamp
  if (updatedAt) {
    // Convert to epoch if it's an ISO string
    const epoch = typeof updatedAt === 'string' 
      ? new Date(updatedAt).getTime() 
      : updatedAt;
    return `${baseUrl}?v=${epoch}`;
  }
  
  // Fallback: use current timestamp (forces refresh but less efficient)
  return `${baseUrl}?v=${Date.now()}`;
};

/**
 * Generate a cache-busted cover URL for a publisher title
 * @param {string} titleId - The title ID  
 * @param {string|number} updatedAt - The cover_updated_at timestamp (optional)
 * @returns {string|null} The cache-busted cover URL
 */
export const getTitleCoverUrl = (titleId, updatedAt = null) => {
  if (!titleId) return null;
  
  const baseUrl = `${API_URL}/api/publisher/titles/${titleId}/cover`;
  
  if (updatedAt) {
    const epoch = typeof updatedAt === 'string' 
      ? new Date(updatedAt).getTime() 
      : updatedAt;
    return `${baseUrl}?v=${epoch}`;
  }
  
  return `${baseUrl}?v=${Date.now()}`;
};

/**
 * Generate a cache-busted cover URL from a book object
 * Handles both manuscripts and titles, including Simple Ingestion
 * @param {Object} book - Book object with id and optionally cover_updated_at
 * @param {string} backendUrl - Backend URL (optional, defaults to API_URL)
 * @returns {string|null} The cache-busted cover URL
 */
export const getBookCoverUrl = (book, backendUrl = null) => {
  if (!book) return null;
  
  const baseUrl = backendUrl || API_URL;
  const id = book.id || book.manuscript_id;
  const updatedAt = book.cover_updated_at || book.updated_at;
  const versionParam = updatedAt ? `?v=${typeof updatedAt === 'string' ? new Date(updatedAt).getTime() : updatedAt}` : `?v=${Date.now()}`;
  
  // Priority 1: Direct cover URLs from backend
  if (book.cover_url) return book.cover_url + versionParam;
  if (book.cover_image_url) return book.cover_image_url + versionParam;
  
  // Priority 2: Simple Ingestion - use job cover endpoint if available
  if (book.simple_ingest_job_id && book.ingestion_method === 'simple_ingest_v1') {
    return `${baseUrl}/api/simple-ingest/jobs/${book.simple_ingest_job_id}/cover${versionParam}`;
  }
  
  // Priority 3: Standard manuscript cover endpoint
  if (id) {
    return `${baseUrl}/api/covers/${id}${versionParam}`;
  }
  
  return null;
};

/**
 * Check if a book has cover image data available
 * @param {Object} book - Book object
 * @returns {boolean} True if cover should be available
 */
export const hasCoverImage = (book) => {
  if (!book) return false;
  
  return !!(
    book.cover_image_path ||
    book.cover_url ||
    book.cover_image_url ||
    book.r2_cover_key ||
    (book.simple_ingest_job_id && book.ingestion_method === 'simple_ingest_v1')
  );
};

/**
 * Extract version parameter for a cover URL
 * Useful when you already have a base URL and need to add cache-busting
 * @param {string|number} updatedAt - Timestamp
 * @returns {string} Version query param (e.g., "?v=1704672000000")
 */
export const getCoverVersionParam = (updatedAt) => {
  if (updatedAt) {
    const epoch = typeof updatedAt === 'string' 
      ? new Date(updatedAt).getTime() 
      : updatedAt;
    return `?v=${epoch}`;
  }
  return `?v=${Date.now()}`;
};

export default {
  getCoverUrl,
  getTitleCoverUrl,
  getBookCoverUrl,
  getCoverVersionParam,
  hasCoverImage
};
